import { useState } from "react";
import { motion } from "framer-motion";
import {
    Phone,
    MessageCircle,
    PlaneLanding,
    ClipboardCheck,
    Luggage,
    Radar,
    Clock3,
    Send,
    User,
    Hash,
    Calendar,
    Clock,
    MapPin,
    Users,
} from "lucide-react";
import { useLang, PHONE_TEL, waLink, LOCATIONS } from "@/i18n";
import { SEO } from "@/components/SEO";
import { Marquee } from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";

const IMGS = {
    hero: "https://images.unsplash.com/photo-1655827763440-7905302b75ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
};

const copy = {
    tr: {
        eyebrow: "ERCAN HAVALİMANI • VIP KARŞILAMA",
        title1: "Ercan Havalimanı",
        title2: "VIP Transfer",
        sub: "Uçağınız indiği andan otelinizin kapısına kadar her detay bizde: isim tabelası ile karşılama, uçuş takibi, ücretsiz bekleme ve Mercedes E-Class konforu.",
        stepsLabel: "KARŞILAMA DENEYİMİ",
        stepsTitle: "İnişten Otele 4 Adım",
        steps: [
            { t: "Uçuş Takibi", d: "Rezervasyonunuzdaki uçuş numarası ile uçağınızı canlı takip ederiz. Rötar olsa bile şoförünüz tam zamanında hazır olur." },
            { t: "İsimle Karşılama", d: "Şoförümüz yolcu çıkış kapısında, üzerinde isminiz yazılı tabela ile sizi bekler. Aramak yok, beklemek yok." },
            { t: "Bagaj Yardımı", d: "Valizleriniz aracınıza özenle yerleştirilir. Siz sadece deri koltuğunuza yerleşirsiniz." },
            { t: "Kapıdan Kapıya", d: "Otelinize, evinize veya istediğiniz adrese kadar klimalı, Wi-Fi'lı VIP yolculuk." },
        ],
        routesLabel: "GÜZERGAHLAR & SÜRELER",
        routesTitle: "Ercan'dan Tüm KKTC'ye",
        routesSub: "Aşağıdaki süreler yaklaşıktır. Kesin fiyat için tek dokunuş yeterli.",
        bookLabel: "TRANSFER REZERVASYONU",
        bookTitle: "Uçuş Bilgilerinizi Gönderin",
        bookSub: "Formu doldurun, tüm detaylar hazır mesaj olarak WhatsApp'a aktarılsın.",
        name: "Ad Soyad",
        flight: "Uçuş No (örn. PC1842)",
        date: "Tarih",
        time: "İniş Saati",
        dest: "Varış Noktası",
        pax: "Kişi",
        send: "WhatsApp ile Gönder",
        call: "veya Hemen Ara",
        ctaFixed: "Sabit Fiyat • Uçuş Takibi • Ücretsiz Bekleme",
    },
    en: {
        eyebrow: "ERCAN AIRPORT • VIP MEET & GREET",
        title1: "Ercan Airport",
        title2: "VIP Transfer",
        sub: "From the moment your plane lands to your hotel door, every detail is handled: name-board welcome, flight tracking, free waiting and Mercedes E-Class comfort.",
        stepsLabel: "THE WELCOME EXPERIENCE",
        stepsTitle: "4 Steps From Landing to Hotel",
        steps: [
            { t: "Flight Tracking", d: "We track your flight live using the flight number in your booking. Even with a delay, your driver is ready right on time." },
            { t: "Meet & Greet", d: "Our driver waits at the arrivals exit holding a board with your name. No searching, no waiting." },
            { t: "Luggage Assistance", d: "Your suitcases are carefully loaded. You simply settle into your leather seat." },
            { t: "Door to Door", d: "An air-conditioned VIP ride with Wi-Fi to your hotel, home or any address." },
        ],
        routesLabel: "ROUTES & DURATIONS",
        routesTitle: "From Ercan to All North Cyprus",
        routesSub: "Durations below are approximate. One tap is enough for an exact price.",
        bookLabel: "TRANSFER BOOKING",
        bookTitle: "Send Your Flight Details",
        bookSub: "Fill the form — all details are forwarded to WhatsApp as a ready message.",
        name: "Full Name",
        flight: "Flight No (e.g. PC1842)",
        date: "Date",
        time: "Landing Time",
        dest: "Destination",
        pax: "Passengers",
        send: "Send via WhatsApp",
        call: "or Call Now",
        ctaFixed: "Fixed Price • Flight Tracking • Free Waiting",
    },
};

const ercanRoutes = [
    ["Girne", "45 dk / 45 min"],
    ["Lefkoşa / Nicosia", "25 dk / 25 min"],
    ["Gazimağusa / Famagusta", "50 dk / 50 min"],
    ["İskele / Iskele", "55 dk / 55 min"],
    ["Bafra", "75 dk / 75 min"],
    ["Güzelyurt", "60 dk / 60 min"],
];

const stepIcons = [Radar, PlaneLanding, Luggage, ClipboardCheck];

export default function ErcanTransfer() {
    const { lang, t } = useLang();
    const c = copy[lang];
    const [form, setForm] = useState({ name: "", flight: "", date: "", time: "", dest: "Girne Merkez", pax: "1" });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = (e) => {
        e.preventDefault();
        const msg =
            lang === "tr"
                ? `Merhaba, Ercan Havalimanı VIP transfer rezervasyonu:\n• Ad: ${form.name}\n• Uçuş No: ${form.flight}\n• Tarih: ${form.date}\n• İniş Saati: ${form.time}\n• Varış: ${form.dest}\n• Kişi: ${form.pax}`
                : `Hello, Ercan Airport VIP transfer booking:\n• Name: ${form.name}\n• Flight No: ${form.flight}\n• Date: ${form.date}\n• Landing: ${form.time}\n• Destination: ${form.dest}\n• Passengers: ${form.pax}`;
        window.open(waLink(msg), "_blank", "noopener");
    };

    const fieldCls =
        "w-full bg-[#0F0F13] border border-white/10 focus:border-[#D4AF37]/60 rounded-lg px-4 py-3 pl-10 text-sm text-[#F7F5F0] outline-none transition-colors duration-300 ff-sans";

    return (
        <main>
            <SEO title={t("seo.ercan.title")} description={t("seo.ercan.desc")} path="/ercan-transfer" />

            <section className="relative min-h-[85svh] flex items-end overflow-hidden" data-testid="ercan-hero">
                <div className="absolute inset-0">
                    <img src={IMGS.hero} alt="Ercan Havalimanı VIP Transfer" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/80 via-[#08080A]/50 to-[#08080A]" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 w-full pb-24 pt-40">
                    <span className="ff-sans text-[11px] md:text-xs tracking-[0.4em] text-[#D4AF37] uppercase block mb-5" data-testid="ercan-eyebrow">
                        {c.eyebrow}
                    </span>
                    <h1 className="ff-serif text-4xl sm:text-5xl lg:text-7xl text-[#F7F5F0] leading-[1.08] tracking-tight" data-testid="ercan-title">
                        <span className="block overflow-hidden pb-1">
                            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}>
                                {c.title1}
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-1">
                            <motion.span className="block italic text-gold-gradient" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}>
                                {c.title2}
                            </motion.span>
                        </span>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8, duration: 0.8 }}
                        className="mt-6 max-w-xl text-base md:text-lg text-zinc-400 font-light ff-sans leading-relaxed"
                        data-testid="ercan-subtitle"
                    >
                        {c.sub}
                    </motion.p>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }} className="mt-9 flex flex-col sm:flex-row gap-4">
                        <a href={PHONE_TEL} data-testid="ercan-call-button" className="gold-glow-hover flex items-center justify-center gap-2.5 rounded-full bg-[#D4AF37] text-[#08080A] font-semibold px-8 py-4 ff-sans text-sm tracking-wide">
                            <Phone size={16} /> +90 533 856 7070
                        </a>
                        <a href={waLink(t("hero.waMsg"))} target="_blank" rel="noopener noreferrer" data-testid="ercan-whatsapp-button" className="gold-glow-hover flex items-center justify-center gap-2.5 rounded-full border border-[#D4AF37]/50 text-[#E6C687] px-8 py-4 ff-sans text-sm tracking-wide bg-[#08080A]/40 backdrop-blur-sm">
                            <MessageCircle size={16} /> WhatsApp
                        </a>
                    </motion.div>
                    <p className="mt-6 ff-sans text-[11px] tracking-[0.25em] uppercase text-zinc-500" data-testid="ercan-fixed-note">{c.ctaFixed}</p>
                </div>
            </section>

            <Marquee />

            <section className="py-24" data-testid="ercan-steps">
                <div className="max-w-7xl mx-auto px-5 lg:px-8">
                    <Reveal className="max-w-2xl mb-14">
                        <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-4">{c.stepsLabel}</span>
                        <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] tracking-tight">{c.stepsTitle}</h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {c.steps.map((s, i) => {
                            const Icon = stepIcons[i];
                            return (
                                <Reveal key={s.t} delay={i * 0.1}>
                                    <div className="gold-glow-hover relative rounded-2xl border border-white/8 bg-[#0F0F13] p-7 h-full" data-testid={`ercan-step-${i}`}>
                                        <span className="ff-accent text-gold-gradient text-4xl absolute top-5 right-6 opacity-40">0{i + 1}</span>
                                        <Icon size={24} className="text-[#D4AF37] mb-5" />
                                        <h3 className="ff-serif text-xl text-[#F7F5F0] mb-2">{s.t}</h3>
                                        <p className="text-sm text-zinc-400 font-light ff-sans leading-relaxed">{s.d}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="py-24 bg-[#0A0A0E] border-y border-white/5" data-testid="ercan-routes">
                <div className="max-w-5xl mx-auto px-5 lg:px-8">
                    <Reveal className="max-w-2xl mb-12">
                        <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-4">{c.routesLabel}</span>
                        <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] tracking-tight">{c.routesTitle}</h2>
                        <p className="mt-4 text-base text-zinc-400 font-light ff-sans">{c.routesSub}</p>
                    </Reveal>
                    <div className="rounded-2xl border border-white/8 overflow-hidden">
                        {ercanRoutes.map(([dest, time], i) => (
                            <Reveal key={dest} delay={i * 0.05}>
                                <a
                                    href={waLink(lang === "tr" ? `Merhaba, Ercan Havalimanı → ${dest} transfer fiyatı almak istiyorum.` : `Hello, I would like a transfer price from Ercan Airport to ${dest}.`)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`ercan-route-row-${i}`}
                                    className={`flex items-center justify-between px-6 md:px-8 py-5 group transition-colors duration-300 hover:bg-[#D4AF37]/5 ${i !== ercanRoutes.length - 1 ? "border-b border-white/5" : ""} bg-[#101014]`}
                                >
                                    <span className="ff-serif text-lg md:text-xl text-[#F7F5F0]">
                                        Ercan <span className="text-[#D4AF37] mx-1">→</span> {dest}
                                    </span>
                                    <span className="flex items-center gap-2 ff-sans text-xs tracking-[0.15em] uppercase text-zinc-400 group-hover:text-[#E6C687] transition-colors">
                                        <Clock3 size={13} className="text-[#D4AF37]" /> {time}
                                    </span>
                                </a>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24" data-testid="ercan-booking">
                <div className="max-w-3xl mx-auto px-5 lg:px-8">
                    <Reveal className="text-center mb-12">
                        <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-4">{c.bookLabel}</span>
                        <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] tracking-tight">{c.bookTitle}</h2>
                        <p className="mt-4 text-base text-zinc-400 font-light ff-sans">{c.bookSub}</p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <form onSubmit={submit} className="rounded-2xl border border-[#D4AF37]/25 bg-[#0F0F13] p-7 md:p-9 grid sm:grid-cols-2 gap-4" data-testid="ercan-booking-form">
                            <label className="relative block">
                                <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]/70" />
                                <input required className={fieldCls} placeholder={c.name} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="ercan-form-name" />
                            </label>
                            <label className="relative block">
                                <Hash size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]/70" />
                                <input required className={fieldCls} placeholder={c.flight} value={form.flight} onChange={(e) => set("flight", e.target.value)} data-testid="ercan-form-flight" />
                            </label>
                            <label className="relative block">
                                <Calendar size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]/70 pointer-events-none" />
                                <input required type="date" className={`${fieldCls} [color-scheme:dark]`} value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="ercan-form-date" />
                            </label>
                            <label className="relative block">
                                <Clock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]/70 pointer-events-none" />
                                <input required type="time" className={`${fieldCls} [color-scheme:dark]`} value={form.time} onChange={(e) => set("time", e.target.value)} data-testid="ercan-form-time" />
                            </label>
                            <label className="relative block">
                                <MapPin size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]/70" />
                                <select className={`${fieldCls} appearance-none`} value={form.dest} onChange={(e) => set("dest", e.target.value)} data-testid="ercan-form-dest">
                                    {LOCATIONS.filter((l) => l !== "Ercan Havalimanı").map((l) => (
                                        <option key={l} value={l}>{l}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="relative block">
                                <Users size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]/70" />
                                <select className={`${fieldCls} appearance-none`} value={form.pax} onChange={(e) => set("pax", e.target.value)} data-testid="ercan-form-pax">
                                    {["1", "2", "3", "4"].map((n) => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </label>
                            <button type="submit" data-testid="ercan-form-submit" className="gold-glow-hover sm:col-span-2 flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-[#08080A] font-semibold py-4 ff-sans text-sm tracking-wide">
                                <Send size={15} /> {c.send}
                            </button>
                            <a href={PHONE_TEL} data-testid="ercan-form-call" className="sm:col-span-2 text-center ff-sans text-sm text-[#E6C687] hover:text-[#F0D270] transition-colors tracking-wide">
                                {c.call}: +90 533 856 7070
                            </a>
                        </form>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
